import { API_BASE_URL } from "../constants";

export type FrameKey =
  | "root"
  | "scenario"
  | "persona"
  | "document"
  | "rules";

export interface PromptFrame {
  key: FrameKey;
  content: string;
  source: "default" | "override";
  requiredSlots: string[];
  allowedSlots: string[];
}

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/** GET /prompt-frames — all five effective frames for the caller. */
export async function getPromptFrames(token?: string): Promise<PromptFrame[]> {
  const response = await fetch(`${API_BASE_URL}/prompt-frames`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to load prompt frames: ${await response.text()}`);
  }
  return response.json();
}

/** PUT /prompt-frames/:key — upsert an override. Throws with the server message on 400. */
export async function updatePromptFrame(
  key: FrameKey,
  content: string,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/prompt-frames/${encodeURIComponent(key)}`,
    {
      method: "PUT",
      headers: authHeaders(token),
      body: JSON.stringify({ content }),
    },
  );
  if (!response.ok) {
    throw new Error(await response.text());
  }
}

/** DELETE /prompt-frames/:key — reset to default. */
export async function resetPromptFrame(
  key: FrameKey,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/prompt-frames/${encodeURIComponent(key)}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to reset frame: ${await response.text()}`);
  }
}
